import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, ClipboardCheck, CalendarDays, FileText, MessageSquare, 
  Target, Briefcase, Sparkles, ChevronRight, Clock
} from 'lucide-react';

const TASK_ICONS = {
  checkin: ClipboardCheck,
  appointment: CalendarDays,
  resume: FileText,
  message: MessageSquare,
  goal: Target,
  opportunity: Briefcase,
  roadmap: Target,
  document: FileText
};

const TASK_COLORS = {
  checkin: 'bg-teal-50 text-teal-600',
  appointment: 'bg-violet-50 text-violet-600',
  resume: 'bg-sky-50 text-sky-600',
  message: 'bg-amber-50 text-amber-600',
  goal: 'bg-emerald-50 text-emerald-600',
  opportunity: 'bg-rose-50 text-rose-600',
  roadmap: 'bg-indigo-50 text-indigo-600',
  document: 'bg-orange-50 text-orange-600'
};

const PRIORITY_BADGES = {
  high: { variant: 'destructive', label: 'Priority' },
  medium: { variant: 'secondary', label: '' },
  low: { variant: 'outline', label: '' }
};

export default function ParticipantActionCenter({ axiosAuth }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const api = axiosAuth();

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const res = await api.get('/my-tasks/next-steps');
      setTasks(res.data.tasks || []);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
    setLoading(false);
  };

  const completeTask = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/complete`);
      loadTasks();
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  };

  const getTaskRoute = (task) => {
    const routes = {
      checkin: '/checkin',
      appointment: '/appointments',
      resume: '/employment',
      message: '/messages',
      goal: '/goals',
      opportunity: '/opportunities',
      roadmap: '/roadmap',
      document: '/documents'
    };
    return routes[task.task_type] || '/dashboard';
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-sky-50 to-violet-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-24">
            <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (tasks.length === 0) {
    return (
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50">
        <CardContent className="p-6 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="font-semibold text-emerald-800">All Caught Up!</h3>
          <p className="text-sm text-emerald-600 mt-1">Great job! You've completed all your tasks.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-sky-50 to-violet-50" data-testid="action-center">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Sparkles className="w-4 h-4 text-violet-500" /> Your Next Steps
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {tasks.slice(0, 5).map((task) => {
          const Icon = TASK_ICONS[task.task_type] || Target;
          const colorClass = TASK_COLORS[task.task_type] || 'bg-gray-50 text-gray-600';
          const isAutoTask = task.id?.startsWith('auto_');
          
          return (
            <div 
              key={task.id} 
              className="flex items-center gap-3 p-3 rounded-xl bg-white/80 hover:bg-white transition-colors cursor-pointer group"
              onClick={() => navigate(getTaskRoute(task))}
              data-testid={`task-${task.task_type}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{task.title}</p>
                {task.description && (
                  <p className="text-xs text-muted-foreground truncate">{task.description}</p>
                )}
              </div>
              {task.priority === 'high' && (
                <Badge className="bg-rose-100 text-rose-700 text-xs shrink-0">Priority</Badge>
              )}
              {task.due_date && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <Clock size={12} />
                  {task.due_date}
                </div>
              )}
              <ChevronRight size={16} className="text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
